import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, MessageSquare, RefreshCw, Pencil, Trash2, X, Check, Reply, CornerDownRight, AlertCircle, Bell, Images, ImagePlus, ChevronDown, ChevronRight, Loader2, ExternalLink } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  Enhancement: { bg: "bg-blue-50 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400", border: "border-blue-200 dark:border-blue-800" },
  Concern: { bg: "bg-orange-50 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400", border: "border-orange-200 dark:border-orange-800" },
  Approval: { bg: "bg-green-50 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", border: "border-green-200 dark:border-green-800" },
  Bug: { bg: "bg-red-50 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", border: "border-red-200 dark:border-red-800" },
  Question: { bg: "bg-purple-50 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400", border: "border-purple-200 dark:border-purple-800" },
  Decision: { bg: "bg-indigo-50 dark:bg-indigo-900/30", text: "text-indigo-700 dark:text-indigo-400", border: "border-indigo-200 dark:border-indigo-800" },
};

const statusColors = {
  Open: { bg: "bg-slate-100 dark:bg-slate-800", text: "text-slate-700 dark:text-slate-300" },
  "In Review": { bg: "bg-amber-50 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" },
  Resolved: { bg: "bg-green-50 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
};

const statusCycle = { Open: "In Review", "In Review": "Resolved", Resolved: "Open" };

const formatTimestamp = (ts) => {
  const date = new Date(ts);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const FeedbackCard = ({ item, stakeholders, onRefresh }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [replyAuthor, setReplyAuthor] = useState("");
  const [replyComment, setReplyComment] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchReplies = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/feedback/${item.id}/replies`);
      setReplies(r.data);
    } catch (e) { console.error(e); }
  }, [item.id]);

  useEffect(() => { fetchReplies(); }, [fetchReplies]);

  const catColor = categoryColors[item.category] || categoryColors.Enhancement;
  const statColor = statusColors[item.status] || statusColors.Open;

  const startEdit = () => {
    setEditingId(item.id);
    setEditData({ stakeholder_name: item.stakeholder_name, category: item.category, comment: item.comment });
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const saveEdit = async () => {
    setSaving(true);
    try { await axios.patch(`${API}/feedback/${item.id}`, editData); cancelEdit(); onRefresh(); } catch (e) { console.error(e); }
    setSaving(false);
  };

  const deleteFeedback = async () => {
    try { await axios.delete(`${API}/feedback/${item.id}`); onRefresh(); } catch (e) { console.error(e); }
  };

  const cycleStatus = async () => {
    try { await axios.patch(`${API}/feedback/${item.id}/status`, { status: statusCycle[item.status] }); onRefresh(); } catch (e) { console.error(e); }
  };

  const toggleNeedsResponse = async () => {
    try { await axios.patch(`${API}/feedback/${item.id}`, { needs_response: !item.needs_response }); onRefresh(); } catch (e) { console.error(e); }
  };

  const submitReply = async () => {
    if (!replyAuthor || !replyComment.trim()) return;
    setReplySubmitting(true);
    try {
      await axios.post(`${API}/feedback/${item.id}/replies`, { author_name: replyAuthor, comment: replyComment });
      setReplyComment("");
      setReplyOpen(false);
      setShowReplies(true);
      await fetchReplies();
    } catch (e) { console.error(e); }
    setReplySubmitting(false);
  };

  const deleteReply = async (rid) => {
    try { await axios.delete(`${API}/feedback/replies/${rid}`); await fetchReplies(); } catch (e) { console.error(e); }
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const sigRes = await axios.get(`${API}/cloudinary/signature?folder=communications&resource_type=auto`);
      const sig = sigRes.data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);
      fd.append("resource_type", "auto");
      const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`, { method: "POST", body: fd });
      const cloudData = await cloudRes.json();
      if (cloudData.secure_url) {
        const newAttachments = [...(item.attachments || []), {
          url: cloudData.secure_url,
          public_id: cloudData.public_id,
          uploaded_at: new Date().toISOString(),
          format: cloudData.format,
          resource_type: cloudData.resource_type,
          original_filename: cloudData.original_filename || file.name,
        }];
        await axios.patch(`${API}/feedback/${item.id}`, { attachments: newAttachments });
        setShowAttachments(true);
        onRefresh();
      } else {
        alert("Upload failed: " + (cloudData.error?.message || "Unknown error"));
      }
    } catch (err) {
      alert("Upload failed: " + err.message);
    }
    setUploading(false);
  };

  const deleteAttachment = async (idx) => {
    const newAttachments = (item.attachments || []).filter((_, i) => i !== idx);
    try {
      await axios.patch(`${API}/feedback/${item.id}`, { attachments: newAttachments });
      onRefresh();
    } catch (e) { console.error(e); }
  };

  return (
    <>
    <Card className={`card-modern transition-all duration-300 ${item.needs_response ? "border-red-300 dark:border-red-700 border-l-4 border-l-red-500" : ""}`} data-testid={`feedback-card-${item.id}`}>
      <CardContent className="p-5">
        {editingId === item.id ? (
          <div className="space-y-3" data-testid={`feedback-edit-form-${item.id}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Editing Feedback</p>
              <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Name</label>
                <Input value={editData.stakeholder_name} onChange={(e) => setEditData(p => ({ ...p, stakeholder_name: e.target.value }))} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="feedback-edit-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Category</label>
                <Select value={editData.category} onValueChange={(val) => setEditData(p => ({ ...p, category: val }))}>
                  <SelectTrigger data-testid="feedback-edit-category" className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    <SelectItem value="Concern" className="dark:text-slate-100 dark:hover:bg-slate-700">Concern</SelectItem>
                    <SelectItem value="Approval" className="dark:text-slate-100 dark:hover:bg-slate-700">Approval</SelectItem>
                    <SelectItem value="Question" className="dark:text-slate-100 dark:hover:bg-slate-700">Question</SelectItem>
                    <SelectItem value="Decision" className="dark:text-slate-100 dark:hover:bg-slate-700">Decision</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Textarea value={editData.comment} onChange={(e) => setEditData(p => ({ ...p, comment: e.target.value }))} rows={3} className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid="feedback-edit-comment" />
            <div className="flex gap-2">
              <Button onClick={saveEdit} disabled={saving} size="sm" className="bg-[#0F172A] hover:bg-slate-800 text-white dark:bg-[#00D4C8] dark:hover:bg-[#00B8AE] dark:text-slate-900" data-testid="feedback-edit-save">
                <Check className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save"}
              </Button>
              <Button onClick={cancelEdit} variant="outline" size="sm" className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-base font-semibold text-slate-800 dark:text-slate-100 font-['Outfit']">{item.stakeholder_name}</span>
                <Badge className="bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800 border text-xs">{item.role && item.role !== "Stakeholder" ? item.role : (stakeholders.find(s => s.name === item.stakeholder_name)?.position || "Stakeholder")}</Badge>
                <Badge className={`${catColor.bg} ${catColor.text} ${catColor.border} border text-xs`}>{item.category}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={toggleNeedsResponse}
                  className={`p-1.5 rounded-md transition-all ${item.needs_response ? "text-red-500 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50" : "text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30"}`}
                  title={item.needs_response ? "Marked as needs response — click to clear" : "Mark as needs response"}
                  data-testid={`feedback-needs-response-${item.id}`}>
                  <Bell className="w-3.5 h-3.5" />
                </button>
                <button onClick={startEdit} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all" data-testid={`feedback-edit-btn-${item.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setDeleteTarget({ fn: deleteFeedback, label: item.comment?.substring(0, 40) })} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all" data-testid={`feedback-delete-btn-${item.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={cycleStatus}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${statColor.bg} ${statColor.text} flex items-center gap-1.5 border border-transparent hover:border-slate-300 dark:hover:border-slate-600`}
                  data-testid={`status-toggle-${item.id}`} title="Click to cycle status">
                  <RefreshCw className="w-3 h-3" />{item.status}
                </button>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-3">{item.comment}</p>

            {/* Attachments Section */}
            <div className="mb-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowAttachments(!showAttachments)}
                  className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                  data-testid={`toggle-attachments-${item.id}`}
                >
                  <Images className="w-4 h-4" />
                  <span>Attachments {(item.attachments || []).length > 0 ? `(${item.attachments.length})` : ""}</span>
                  {showAttachments ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.heic,.heif"
                    className="hidden"
                    onChange={(e) => { uploadAttachment(e.target.files?.[0]); e.target.value = ""; }}
                    data-testid={`attachment-input-${item.id}`}
                  />
                  <span className="flex items-center gap-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors" data-testid={`add-file-btn-${item.id}`}>
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                    {uploading ? "Uploading..." : "Add File"}
                  </span>
                </label>
              </div>
              {showAttachments && (item.attachments || []).length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(item.attachments || []).map((att, idx) => (
                    <div key={idx} className="relative group" data-testid={`attachment-${item.id}-${idx}`}>
                      {att.format === "pdf" || att.resource_type === "raw" ? (
                        <a href={att.url} target="_blank" rel="noopener noreferrer" className="w-full h-32 flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors">
                          <span className="text-2xl mb-1">&#128196;</span>
                          <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[90%] px-1">{att.original_filename || "Document"}</span>
                        </a>
                      ) : (
                        <img src={att.url} alt={`Attachment ${idx + 1}`} className="w-full h-32 object-cover rounded-lg border border-slate-200 dark:border-slate-700" />
                      )}
                      <button
                        onClick={() => deleteAttachment(idx)}
                        className="absolute top-1 right-1 p-1 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete"
                        data-testid={`delete-attachment-${item.id}-${idx}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <a href={att.url} target="_blank" rel="noopener noreferrer" className="absolute bottom-1 left-1 text-[10px] bg-black/50 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        <ExternalLink className="w-2.5 h-2.5" />View
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {showAttachments && (item.attachments || []).length === 0 && (
                <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">No attachments yet. Click "Add File" to upload.</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400 dark:text-slate-500">{formatTimestamp(item.timestamp)}</p>
              <div className="flex items-center gap-3">
                {replies.length > 0 && (
                  <button onClick={() => setShowReplies(!showReplies)} className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 flex items-center gap-1" data-testid={`toggle-replies-${item.id}`}>
                    <MessageSquare className="w-3 h-3" />
                    {replies.length} {replies.length === 1 ? "reply" : "replies"}
                  </button>
                )}
                <button onClick={() => { setReplyOpen(!replyOpen); setShowReplies(true); }} className="text-xs text-[#00D4C8] hover:text-teal-600 font-medium flex items-center gap-1" data-testid={`reply-btn-${item.id}`}>
                  <Reply className="w-3 h-3" /> Reply
                </button>
              </div>
            </div>

            {/* Replies */}
            {showReplies && replies.length > 0 && (
              <div className="mt-4 ml-6 border-l-2 border-slate-100 dark:border-slate-700 pl-4 space-y-3" data-testid={`replies-list-${item.id}`}>
                {replies.map((r) => (
                  <div key={r.id} className="flex items-start gap-2" data-testid={`reply-${r.id}`}>
                    <CornerDownRight className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{r.author_name}</span>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500">{formatTimestamp(r.created_at)}</span>
                        </div>
                        <button onClick={() => deleteReply(r.id)} className="p-1 rounded text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30" data-testid={`delete-reply-${r.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{r.comment}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Reply Form */}
            {replyOpen && (
              <div className="mt-4 ml-6 border-l-2 border-[#00D4C8]/30 pl-4 space-y-2" data-testid={`reply-form-${item.id}`}>
                <Select value={replyAuthor} onValueChange={setReplyAuthor}>
                  <SelectTrigger className="h-8 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid={`reply-author-${item.id}`}><SelectValue placeholder="Select your name" /></SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    {stakeholders.map((s) => (
                      <SelectItem key={s.id} value={s.name} className="dark:text-slate-100 dark:hover:bg-slate-700">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea placeholder="Write your reply..." rows={2} value={replyComment} onChange={(e) => setReplyComment(e.target.value)} className="text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" data-testid={`reply-comment-${item.id}`} />
                <div className="flex gap-2">
                  <Button onClick={submitReply} disabled={replySubmitting || !replyAuthor || !replyComment.trim()} size="sm" className="h-7 bg-[#0F172A] hover:bg-slate-800 text-white dark:bg-[#00D4C8] dark:hover:bg-[#00B8AE] dark:text-slate-900 text-xs" data-testid={`reply-submit-${item.id}`}>
                    <Send className="w-3 h-3 mr-1" />{replySubmitting ? "Sending..." : "Reply"}
                  </Button>
                  <Button onClick={() => setReplyOpen(false)} variant="outline" size="sm" className="h-7 text-xs dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget?.fn()} title="Delete Communication" description={`Are you sure you want to delete this communication? This action cannot be undone.`} />
    </>
  );
};

export const StakeholderFeedbackPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, breakdown: {} });
  const [formData, setFormData] = useState({ stakeholder_name: "", title: "", category: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [stakeholders, setStakeholders] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchFeedback = useCallback(async () => {
    try {
      const params = productId ? { product_id: productId } : {};
      const [itemsRes, summaryRes, stakeholdersRes] = await Promise.all([
        axios.get(`${API}/feedback`, { params }),
        axios.get(`${API}/feedback/summary`, { params }),
        axios.get(`${API}/stakeholders`, { params }),
      ]);
      setFeedbackItems(itemsRes.data);
      setSummary(summaryRes.data);
      setStakeholders(stakeholdersRes.data);
    } catch (err) { console.error(err); }
  }, [productId]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.stakeholder_name || !formData.category || !formData.comment) return;
    setSubmitting(true);
    const category = formData.category;
    try {
      await axios.post(`${API}/feedback`, { ...formData, role: formData.title || "Stakeholder", product_id: productId });
      setFormData({ stakeholder_name: "", title: "", category: "", comment: "" });
      setShowForm(false);
      await fetchFeedback();
      if (category === "Bug" || category === "Enhancement") {
        setShowTicketModal(true);
      }
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  return (
    <div data-testid="feedback-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-3">Validation</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Communications</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Stakeholder input and communications</p>
      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {summary.total > 0 && (
        <Card className="card-modern mb-6" data-testid="feedback-summary">
          <CardContent className="p-5 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{summary.total} Item{summary.total !== 1 ? "s" : ""}</span>
            </div>
            <Separator orientation="vertical" className="h-5 dark:bg-slate-700" />
            <div className="flex items-center gap-2 flex-wrap">
              {filterCategory && (
                <button onClick={() => setFilterCategory(null)} className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 underline mr-1" data-testid="clear-filter">Clear filter</button>
              )}
              {Object.entries(summary.breakdown).map(([cat, count]) => {
                const colors = categoryColors[cat] || categoryColors.Concern;
                const isActive = filterCategory === cat;
                return (
                  <Badge key={cat} onClick={() => setFilterCategory(isActive ? null : cat)} data-testid={`filter-${cat.toLowerCase()}`}
                    className={`cursor-pointer transition-all ${colors.bg} ${colors.text} ${colors.border} border ${isActive ? "ring-2 ring-offset-1 ring-offset-transparent ring-current scale-105" : "opacity-80 hover:opacity-100"}`}>
                    {cat}: {count}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-4" data-testid="feedback-cards-list">
        {(filterCategory ? feedbackItems.filter(item => item.category === filterCategory) : feedbackItems).map((item) => (
          <FeedbackCard key={item.id} item={item} stakeholders={stakeholders} onRefresh={fetchFeedback} />
        ))}
      </div>

      {feedbackItems.length === 0 && !showForm && (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500" data-testid="no-feedback">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No communications yet. Click the button below to get started.</p>
        </div>
      )}

      {showForm ? (
        <Card className="card-modern mt-6" data-testid="feedback-form-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 font-['Outfit']">Submit Communication</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" data-testid="close-form-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="feedback-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Stakeholder Name</label>
                  <Select value={formData.stakeholder_name} onValueChange={(val) => {
                    const found = stakeholders.find(s => s.name === val);
                    setFormData((prev) => ({ ...prev, stakeholder_name: val, title: found?.position || "" }));
                  }}>
                    <SelectTrigger data-testid="input-stakeholder-name" className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"><SelectValue placeholder="Select stakeholder" /></SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      {stakeholders.map((s) => (<SelectItem key={s.id} value={s.name} className="dark:text-slate-100 dark:hover:bg-slate-700">{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Title</label>
                  <Input value={formData.title} disabled className="bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300" data-testid="select-role" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Category</label>
                  <Select value={formData.category} onValueChange={(val) => setFormData((prev) => ({ ...prev, category: val }))}>
                    <SelectTrigger data-testid="select-category" className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      <SelectItem value="Concern" className="dark:text-slate-100 dark:hover:bg-slate-700">Concern</SelectItem>
                      <SelectItem value="Approval" className="dark:text-slate-100 dark:hover:bg-slate-700">Approval</SelectItem>
                      <SelectItem value="Question" className="dark:text-slate-100 dark:hover:bg-slate-700">Question</SelectItem>
                      <SelectItem value="Decision" className="dark:text-slate-100 dark:hover:bg-slate-700">Decision</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Comment</label>
                <Textarea data-testid="textarea-comment" placeholder="Share your feedback, concerns, or suggestions..." rows={4}
                  value={formData.comment} onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))} 
                  className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100" />
              </div>
              <Button type="submit" disabled={submitting || !formData.stakeholder_name || !formData.category || !formData.comment}
                className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold disabled:opacity-50" data-testid="submit-feedback-btn">
                <Send className="w-4 h-4 mr-2" />{submitting ? "Submitting..." : "Submit"}
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-6">
          <Button onClick={() => setShowForm(true)} className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold" data-testid="add-new-comm-btn">
            <Send className="w-4 h-4 mr-2" /> Add New Comm
          </Button>
        </div>
      )}

      {/* Bug/Enhancement Ticket Modal */}
      {showTicketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="ticket-modal-overlay">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 dark:border dark:border-slate-700" data-testid="ticket-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 font-['Outfit']">Action Required</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              For bugs or enhancement requests, please submit a ticket to{" "}
              <a href="mailto:salesforce@usa.edu" className="text-[#00D4C8] font-semibold hover:underline">salesforce@usa.edu</a>.
              The IT team will review and prioritize the request based on urgency and impact.
            </p>
            <Button onClick={() => setShowTicketModal(false)} className="w-full bg-[#0F172A] hover:bg-slate-800 text-white dark:bg-[#00D4C8] dark:hover:bg-[#00B8AE] dark:text-slate-900" data-testid="ticket-modal-agree-btn">
              Agree
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
